import React, { useEffect, useState, useRef } from 'react';
import { format, parseISO } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import Menu from '../menu';

const formatDataBR = (dataString) => {
  try {
    const date = parseISO(dataString);
    return format(date, 'dd/MM/yyyy HH:mm', { locale: ptBR });
  } catch {
    return dataString;
  }
};

const corBadge = (status) => {
  switch (status?.toLowerCase()) {
    case 'sucesso':
      return 'bg-success text-white';
    case 'erro':
    case 'falha':
      return 'bg-danger text-white';
    case 'aviso':
    case 'pendente':
      return 'bg-warning text-dark';
    default:
      return 'bg-secondary text-white';
  }
};

const TimelineButton = ({ auditoria, selected, onClick }) => (
  <button
    onClick={onClick}
    className={`btn btn-sm rounded-pill me-2 transition-btn ${
      selected ? 'btn-primary text-white shadow-sm' : 'btn-outline-primary'
    }`}
    style={{ minWidth: 200, flex: '0 0 auto' }}
    aria-pressed={selected}
    type="button"
  >
    🗓 {formatDataBR(auditoria.DataAuditoria)}{' '}
    <span className={`badge ms-2 ${corBadge(auditoria.Status)}`}>{auditoria.Status}</span>
  </button>
);

const Auditoria = () => {
  const [auditorias, setAuditorias] = useState([]);
  const [detalhe, setDetalhe] = useState(null);
  const [erro, setErro] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filtroFaltantes, setFiltroFaltantes] = useState(false);
  const [filtroSemCupons, setFiltroSemCupons] = useState(false);
  const [idSelecionado, setIdSelecionado] = useState(null);
  const [buscaNome, setBuscaNome] = useState('');

  const timelineRef = useRef(null);

  useEffect(() => {
    fetch('https://portal.compsoft.com.br/portalcompsoft/apibuscaauditoria.aspx')
      .then((res) => res.json())
      .then((res) => {
        console.log('Auditorias brutas:', res); // Para debug
        // Filtra auditorias que não têm CNPJ 11111111111111
        const auditoriasFiltradas = res.filter(auditoria => {
          console.log('Verificando auditoria:', auditoria.CNPJ); // Para debug
          return auditoria.CNPJ !== '11111111111111';
        });
        console.log('Auditorias filtradas:', auditoriasFiltradas); // Para debug
        setAuditorias(auditoriasFiltradas);
      })
      .catch(() => setErro('Erro ao carregar auditorias'));
  }, []);

  const carregarDetalhe = (id) => {
    setErro(null);
    setDetalhe(null);
    setLoading(true);
    setIdSelecionado(id);
    fetch(`https://portal.compsoft.com.br/portalcompsoft/apibuscaauditoria.aspx?id=${id}`)
      .then((res) => res.json())
      .then((data) => {
        console.log('Detalhe bruto:', data); // Para debug
        if (data.Erro) {
          setErro(`❌ Oops! Não foi possível efetuar a auditoria: ${data.Erro}`);
          setDetalhe(null);
        } else if (Array.isArray(data) && data.length === 0) {
          setErro('⚠️ Ops... Não conseguimos obter os dados desta auditoria.');
          setDetalhe(null);
        } else if (data === null || data === '' || (Array.isArray(data) && data.length === 0)) {
          setErro('⚠️ Ops... Não conseguimos obter os dados desta auditoria.');
          setDetalhe(null);
        } else {
          // Filtra os itens do detalhe que não têm CNPJ 11111111111111
          let detalheFiltrado;
          if (Array.isArray(data)) {
            detalheFiltrado = data.filter(item => {
              console.log('Filtrando item detalhe:', item.CNPJ); // Para debug
              return item.CNPJ !== '11111111111111';
            });
          } else {
            // Se não for array, verifica se é um objeto único
            detalheFiltrado = data.CNPJ !== '11111111111111' ? data : null;
          }
          
          console.log('Detalhe filtrado:', detalheFiltrado); // Para debug
          
          if (!detalheFiltrado || (Array.isArray(detalheFiltrado) && detalheFiltrado.length === 0)) {
            setErro('⚠️ Ops... Não há dados disponíveis para esta auditoria após filtro.');
            setDetalhe(null);
          } else {
            setDetalhe(detalheFiltrado);
            setErro(null);
          }
        }
        setLoading(false);
      })
      .catch(() => {
        setErro('❌ Erro ao carregar detalhe da auditoria. Tente novamente mais tarde.');
        setDetalhe(null);
        setLoading(false);
      });
  };

  // Função auxiliar para filtrar CNPJ indesejado
  const filtrarCNPJIndesejado = (dados) => {
    if (!dados) return [];
    
    if (Array.isArray(dados)) {
      return dados.filter(item => {
        // Verifica de múltiplas formas
        const cnpj = item.CNPJ || item.cnpj || item.Cnpj;
        return cnpj !== '11111111111111';
      });
    }
    
    // Se não for array, verifica se é objeto único
    const cnpj = dados.CNPJ || dados.cnpj || dados.Cnpj;
    return cnpj !== '11111111111111' ? [dados] : [];
  };

  const detalheFiltrado = detalhe
    ? filtrarCNPJIndesejado(detalhe)
        .map((item) => {
          let seriesFiltradas = item.Series || [];

          if (filtroSemCupons) {
            seriesFiltradas = seriesFiltradas.filter(
              (serie) => !(serie.Obs?.includes('Último Cupom emitido: 0'))
            );
          }

          if (filtroFaltantes) {
            seriesFiltradas = seriesFiltradas.filter(
              (serie) => serie.ChecagensFaltantes?.[0]?.Faltantes?.length > 0
            );
          }

          return {
            ...item,
            Series: seriesFiltradas,
          };
        })
        .filter(
          (item) =>
            item.Series.length > 0 && item.Nome?.toLowerCase().includes(buscaNome.toLowerCase())
        )
        .sort((a, b) => a.Nome.localeCompare(b.Nome))
    : [];

  // Usa a função auxiliar para filtrar as auditorias também
  const auditoriasFiltradas = filtrarCNPJIndesejado(auditorias);

  const scrollTimeline = (direction) => {
    if (!timelineRef.current) return;
    const scrollAmount = 220;
    timelineRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  return (
    <>
      <Menu />
      <div className="container" style={{ marginTop: '70px' }}>
        <header
          className="bg-white border-bottom sticky-top"
          style={{ top: '70px', zIndex: 1050, padding: '0.75rem 1rem' }}
        >
          <h6 className="mb-2">Auditorias Recentes</h6>

          <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 mb-2">
            <input
              type="text"
              className="form-control flex-grow-1"
              placeholder="Buscar auditoria por nome..."
              value={buscaNome}
              onChange={(e) => setBuscaNome(e.target.value)}
            />
            <div className="form-check form-switch ms-md-3">
              <input
                className="form-check-input"
                type="checkbox"
                id="filtroFaltantes"
                checked={filtroFaltantes}
                onChange={() => setFiltroFaltantes(!filtroFaltantes)}
              />
              <label
                className="form-check-label"
                htmlFor="filtroFaltantes"
                style={{ fontSize: '0.75em' }}
              >
                Mostrar apenas faltantes
              </label>
            </div>
            <div className="form-check form-switch ms-md-3">
              <input
                className="form-check-input"
                type="checkbox"
                id="filtroSemCupons"
                checked={filtroSemCupons}
                onChange={() => setFiltroSemCupons(!filtroSemCupons)}
              />
              <label
                className="form-check-label"
                htmlFor="filtroSemCupons"
                style={{ fontSize: '0.75em' }}
              >
                Ocultar séries sem cupons emitidos
              </label>
            </div>
          </div>

          <div className="d-flex align-items-center position-relative">
            <button
              type="button"
              className="btn btn-outline-secondary me-2"
              onClick={() => scrollTimeline('left')}
              aria-label="Scroll timeline para esquerda"
            >
              ‹
            </button>
            <div
              ref={timelineRef}
              className="d-flex overflow-x-auto"
              style={{ scrollSnapType: 'x mandatory', gap: '0.5rem', flexGrow: 1 }}
            >
              <style>{`div::-webkit-scrollbar { display: none; }`}</style>

              {auditoriasFiltradas.length === 0 ? (
                <span className="text-muted">Nenhuma auditoria encontrada</span>
              ) : (
                auditoriasFiltradas.map((a) => (
                  <TimelineButton
                    key={a.Id}
                    auditoria={a}
                    selected={idSelecionado === a.Id}
                    onClick={() => carregarDetalhe(a.Id)}
                  />
                ))
              )}
            </div>
            <button
              type="button"
              className="btn btn-outline-secondary ms-2"
              onClick={() => scrollTimeline('right')}
              aria-label="Scroll timeline para direita"
            >
              ›
            </button>
          </div>
        </header>

        <main style={{ paddingTop: '1rem', maxHeight: '70vh', overflowY: 'auto' }}>
          {erro && (
            <div
              className="alert alert-warning d-flex align-items-center"
              role="alert"
              style={{ fontSize: '1.1rem', gap: '0.5rem' }}
            >
              <span aria-hidden="true" style={{ fontSize: '1.5rem' }}>
                ⚠️
              </span>
              <div>{erro}</div>
            </div>
          )}
          {loading && <p>Carregando detalhes...</p>}

          {!loading && detalheFiltrado.length > 0 ? (
            detalheFiltrado.map((item, index) => {
              // Verificação adicional para garantir que não mostra CNPJ indesejado
              const cnpj = item.CNPJ || item.cnpj || item.Cnpj;
              if (cnpj === '11111111111111') {
                return null; // Não renderiza este item
              }
              
              return (
                <div
                  key={index}
                  className="mb-4 p-3 rounded shadow-sm"
                  style={{
                    display: 'flex',
                    gap: '1.5rem',
                    backgroundColor: '#f8f9fa',
                    borderLeft: '5px solid #0d6efd',
                    transition: 'background-color 0.3s ease',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#e9ecef')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#f8f9fa')}
                >
                  <div style={{ width: '320px', flexShrink: 0 }}>
                    <div className="d-flex align-items-center mb-2">
                      <div className="fs-3 me-3" aria-label="Ícone de usuário">
                        👤
                      </div>
                      <div>
                        <div className="text-muted" style={{ fontSize: '0.8em' }}>
                          {cnpj || 'CNPJ não disponível'}
                        </div>
                        <div className="fw-bold" style={{ fontSize: '1.1em' }}>
                          {item.Nome || 'Nome não informado'}
                        </div>
                      </div>
                    </div>
                  </div>

                  <ul
                    className="list-unstyled flex-grow-1"
                    style={{ margin: 0 }}
                    aria-label={`Séries da auditoria de ${item.Nome}`}
                  >
                    {item.Series.map((serie, i) => {
                      const faltantes = serie.ChecagensFaltantes?.[0]?.Faltantes || [];
                      const hasFaltantes = faltantes.length > 0;
                      const isZeroCupom = serie.Obs?.includes('Último Cupom emitido: 0') ?? false;

                      return (
                        <li
                          key={i}
                          className="d-flex align-items-center gap-3 mb-2 p-2 rounded"
                          style={{
                            backgroundColor: hasFaltantes ? '#fff3cd' : '#d1e7dd',
                            border: `1px solid ${hasFaltantes ? '#ffeeba' : '#badbcc'}`,
                            boxShadow: '0 1px 3px rgb(0 0 0 / 0.1)',
                          }}
                          aria-label={`Série ${serie.Codigo}, ${
                            hasFaltantes ? 'com faltantes' : 'sem faltantes'
                          }`}
                        >
                          <span className="fw-semibold" style={{ minWidth: '120px' }}>
                            📄 Série {serie.Codigo}
                          </span>
                          <span
                            aria-hidden="true"
                            style={{ fontSize: '1.2em', color: hasFaltantes ? '#856404' : '#0f5132' }}
                          >
                            {hasFaltantes ? '⚠️' : '✔️'}
                          </span>
                          <span
                            style={{
                              flexGrow: 1,
                              fontWeight: hasFaltantes ? '600' : '400',
                              color: hasFaltantes ? '#856404' : '#0f5132',
                            }}
                          >
                            {hasFaltantes ? `Faltantes: ${faltantes.join(', ')}` : 'Sem faltantes'}
                          </span>
                          <span
                            className="text-muted"
                            style={{
                              fontSize: '0.75em',
                              fontWeight: isZeroCupom ? 'bold' : 'normal',
                              color: isZeroCupom ? 'crimson' : 'inherit',
                            }}
                          >
                            {isZeroCupom
                              ? '⚠️ Não foram emitidos cupons no período auditado'
                              : serie.Obs || ''}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })
          ) : (
            !loading &&
            !erro &&
            detalhe !== null && (
              <div
                className="d-flex flex-column align-items-center justify-content-center p-4 border rounded"
                style={{
                  backgroundColor: '#fff3cd',
                  color: '#856404',
                  fontWeight: '600',
                  fontSize: '1.1rem',
                  minHeight: '150px',
                }}
              >
                <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>😕</div>
                <div>Ops... Não conseguimos obter os dados desta auditoria.</div>
                <small className="text-muted mt-1">
                  Tente selecionar outra auditoria ou recarregar a página.
                </small>
              </div>
            )
          )}
        </main>
      </div>
    </>
  );
};

export default Auditoria;